// import { Component } from '@theme/component';
// import { RecentlyViewed } from '@theme/recently-viewed-products';
// import { sectionRenderer } from '@theme/section-renderer';

// /**
//  * Section component that hydrates itself with the merchant's recently-viewed
//  * products. IDs live in localStorage; the actual cards are rendered server-side
//  * by fetching this section against `/search?q=id:<id>` via the Section
//  * Rendering API (same idiom the predictive search uses).
//  *
//  * @extends {Component<{}>}
//  */
// class RecentlyViewedProductsSection extends Component {
//   #hydrated = false;
//   /** @type {AbortController | null} */
//   #abortController = null;

//   connectedCallback() {
//     super.connectedCallback();

//     if (this.#hydrated) return;
//     this.#hydrated = true;

//     // In the theme editor we intentionally skip hydration so the merchant
//     // always sees a consistent placeholder preview instead of cards pulled
//     // from whatever products they happened to browse in the editor session.
//     if (window.Shopify?.designMode) return;

//     this.#hydrate();
//   }

//   disconnectedCallback() {
//     super.disconnectedCallback();
//     this.#abortController?.abort();
//   }

//   async #hydrate() {
//     const { sectionId, excludeProductId } = this.dataset;
//     const maxProducts = Number.parseInt(this.dataset.maxProducts ?? '4', 10) || 4;

//     if (!sectionId) return;

//     const viewedIds = RecentlyViewed.getProducts().filter(
//       /** @param {string} id */ (id) => id && id !== excludeProductId
//     );

//     if (viewedIds.length === 0) {
//       this.#remove();
//       return;
//     }

//     this.#setLoading(true);

//     const ids = viewedIds.slice(0, maxProducts);
//     const url = new URL(Theme.routes.search_url, location.origin);
//     url.searchParams.set('q', ids.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
//     url.searchParams.set('resources[type]', 'product');

//     try {
//       this.#abortController?.abort();
//       this.#abortController = new AbortController();

//       await sectionRenderer.renderSection(sectionId, { cache: false, url });

//       const remainingCards = this.querySelectorAll('.resource-list__item').length;
//       if (remainingCards === 0) {
//         this.#remove();
//         return;
//       }

//       const wrapper = this.closest('.shopify-section');
//       if (wrapper instanceof HTMLElement) wrapper.hidden = false;
//       this.hidden = false;
//       this.#setLoading(false);
//     } catch (error) {
//       if (error instanceof DOMException && error.name === 'AbortError') return;
//       console.error('Recently viewed products error:', error);
//       this.#remove();
//     }
//   }

//   #remove() {
//     const wrapper = this.closest('.shopify-section');
//     if (wrapper instanceof HTMLElement) {
//       wrapper.hidden = true;
//       return;
//     }
//     this.hidden = true;
//   }

//   /**
//    * Shows a loading state while the section-rendering request fetches product cards.
//    * @param {boolean} isLoading
//    */
//   #setLoading(isLoading) {
//     this.toggleAttribute('data-loading', isLoading);
//     this.setAttribute('aria-busy', String(isLoading));

//     if (isLoading) {
//       const wrapper = this.closest('.shopify-section');
//       if (wrapper instanceof HTMLElement) wrapper.hidden = false;
//       this.hidden = false;
//     }
//   }
// }

// if (!customElements.get('recently-viewed-products')) {
//   customElements.define('recently-viewed-products', RecentlyViewedProductsSection);
// }



import { Component } from '@theme/component';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * Section component that hydrates itself with the merchant's recently-viewed
 * products. IDs live in localStorage; the actual cards are rendered server-side
 * by fetching this section against `/search?q=id:<id>` via the Section
 * Rendering API (same idiom the predictive search uses).
 *
 * @extends {Component<{}>}
 */
class RecentlyViewedProductsSection extends Component {
  #hydrated = false;
  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    super.connectedCallback();

    if (this.#hydrated) return;
    this.#hydrated = true;

    // In the theme editor we intentionally skip hydration so the merchant
    // always sees a consistent placeholder preview instead of cards pulled
    // from whatever products they happened to browse in the editor session.
    if (window.Shopify?.designMode) return;

    this.#hydrate();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  async #hydrate() {
    const { sectionId, excludeProductId } = this.dataset;
    const maxProducts = Number.parseInt(this.dataset.maxProducts ?? '4', 10) || 4;

    if (!sectionId) return;

    const viewedIds = RecentlyViewed.getProducts().filter(
      /** @param {string} id */ (id) => id && id !== excludeProductId
    );

    if (viewedIds.length === 0) {
      // NOTE: user has no recently viewed products at all → show empty message
      this.#showEmpty();
      return;
    }

    this.#setLoading(true);

    const ids = viewedIds.slice(0, maxProducts);
    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', ids.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    try {
      this.#abortController?.abort();
      this.#abortController = new AbortController();

      await sectionRenderer.renderSection(sectionId, { cache: false, url });

      const remainingCards = this.querySelectorAll('.resource-list__item').length;
      if (remainingCards === 0) {
        // NOTE: ids existed but products no longer resolve (deleted/unpublished) → show empty message
        this.#showEmpty();
        return;
      }

      const wrapper = this.closest('.shopify-section');
      if (wrapper instanceof HTMLElement) wrapper.hidden = false;
      this.hidden = false;
      this.#setLoading(false);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      console.error('Recently viewed products error:', error);
      this.#remove();
    }
  }

  #remove() {
    const wrapper = this.closest('.shopify-section');
    if (wrapper instanceof HTMLElement) {
      wrapper.hidden = true;
      return;
    }
    this.hidden = true;
  }

  /**
   * Shows the empty-state message instead of hiding the whole section.
   * Keeps the section visible, hides loading + any leftover product cards.
   */
  #showEmpty() {
    const wrapper = this.closest('.shopify-section');
    if (wrapper instanceof HTMLElement) wrapper.hidden = false;
    this.hidden = false;

    this.#setLoading(false);

    const emptyEl = this.querySelector('[data-recently-viewed-empty]');
    if (emptyEl instanceof HTMLElement) emptyEl.hidden = false;

    const resourceList = this.querySelector('.section-resource-list__content')?.previousElementSibling;
    // hide the resource-list grid/carousel if present
    this.querySelectorAll('.resource-list__item').forEach((el) => {
      if (el instanceof HTMLElement) el.hidden = true;
    });
  }

  /**
   * Shows a loading state while the section-rendering request fetches product cards.
   * @param {boolean} isLoading
   */
  #setLoading(isLoading) {
    this.toggleAttribute('data-loading', isLoading);
    this.setAttribute('aria-busy', String(isLoading));

    if (isLoading) {
      const wrapper = this.closest('.shopify-section');
      if (wrapper instanceof HTMLElement) wrapper.hidden = false;
      this.hidden = false;

      const emptyEl = this.querySelector('[data-recently-viewed-empty]');
      if (emptyEl instanceof HTMLElement) emptyEl.hidden = true;
    }
  }
}

if (!customElements.get('recently-viewed-products')) {
  customElements.define('recently-viewed-products', RecentlyViewedProductsSection);
}